import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Check, ArrowRight, ShoppingBag } from "lucide-react";
import { ROUTES, C, SERIF } from "@/lib/publicLinks";
import { useCart } from "@/context/CartContext";
import { DEFAULT_SITE, useSiteContent } from "@/lib/siteContent";

export const Eyebrow = ({ children, dark = false }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="h-px w-8" style={{ background: dark ? C.lavender : C.accent }} />
    <span className="text-[0.7rem] font-semibold tracking-[0.2em] uppercase" style={{ color: dark ? C.lavender : C.accent }}>{children}</span>
  </div>
);

export const PrimaryBtn = ({ to, href, children, className = "", ...p }) => {
  const cls = `inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition shadow-sm hover:opacity-90 ${className}`;
  const style = { background: C.primary };
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style} {...p}>{children}</a>;
  return <Link to={to} className={cls} style={style} {...p}>{children}</Link>;
};

export const GhostBtn = ({ to, href, children, className = "", ...p }) => {
  const cls = `inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition bg-white hover:bg-slate-50 ${className}`;
  const style = { color: C.primary, border: `1px solid ${C.line}` };
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style} {...p}>{children}</a>;
  return <Link to={to} className={cls} style={style} {...p}>{children}</Link>;
};

export const CheckItem = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center" style={{ background: "#EAF6F0", color: C.green }}>
      <Check className="h-3 w-3" strokeWidth={3} />
    </span>
    <span className="text-slate-700">{children}</span>
  </li>
);

export const H2 = ({ children, className = "", style = {} }) => (
  <h2 className={`text-3xl sm:text-4xl leading-tight text-slate-900 ${className}`} style={{ ...SERIF, ...style }}>{children}</h2>
);

function CartButton({ mobile = false }) {
  const { count } = useCart();
  return (
    <Link to={ROUTES.cart} className="relative inline-flex items-center" data-testid="nav-cart-button" aria-label="Cart">
      <ShoppingBag className="h-5 w-5" style={{ color: mobile ? C.lavender : "#fff" }} />
      {count > 0 && (
        <span className="absolute -top-2 -right-2 h-4 min-w-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
          style={{ background: C.green, color: "#fff" }} data-testid="nav-cart-count">{count}</span>
      )}
    </Link>
  );
}

function PublicNav({ nav }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const items = (nav?.items || []).filter((n) => n.visible !== false);
  const ctaLabel = nav?.ctaLabel || "Get Started";
  const ctaTo = nav?.ctaTo || ROUTES.navigator;
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <header className="sticky top-0 z-50" style={{ background: C.plum }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={ROUTES.home} className="flex items-center gap-2 text-white" data-testid="public-logo">
          <img src="/media/kissed/kissed-logo.png" alt="KISSED" className="h-10 w-auto" />
          <span className="leading-none hidden sm:block">
            <span className="block text-[0.6rem] tracking-[0.18em] uppercase" style={{ color: C.lavender }}>Trichology &amp; Functional Wellness</span>
          </span>
        </Link>
        <nav className="hidden xl:flex items-center gap-5">
          {items.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`public-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className="text-sm transition hover:text-white whitespace-nowrap"
              style={{ color: loc.pathname === n.to ? "#fff" : C.lavender }}>
              {n.label}
            </Link>
          ))}
          <CartButton />
          <PrimaryBtn to={ctaTo} className="!py-2 !px-5 !bg-white !text-[#2E1245] whitespace-nowrap" data-testid="public-nav-cta">{ctaLabel}</PrimaryBtn>
        </nav>
        <div className="xl:hidden flex items-center gap-4">
          <CartButton mobile />
          <button className="text-white" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="public-nav-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t max-h-[calc(100vh-4rem)] overflow-y-auto" style={{ background: C.plum, borderColor: "rgba(255,255,255,0.1)" }} data-testid="public-mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {items.map((n) => (
              <Link key={n.to} to={n.to} className="py-3 text-base border-b" style={{ color: "#fff", borderColor: "rgba(255,255,255,0.08)" }} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{n.label}</Link>
            ))}
            <Link to={ROUTES.programs} className="py-3 text-base" style={{ color: C.lavender }} data-testid="mobile-nav-programs">Programs</Link>
            <PrimaryBtn to={ctaTo} className="mt-3 !bg-white !text-[#2E1245]" data-testid="mobile-nav-cta">{ctaLabel}</PrimaryBtn>
          </div>
        </div>
      )}
    </header>
  );
}

function PublicFooter({ nav, footer }) {
  const items = (nav?.items || []).filter((n) => n.visible !== false);
  const f = footer || {};
  return (
    <footer style={{ background: C.plum }} className="text-white mt-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-10 md:grid-cols-3">
          <div>
            <div className="text-2xl" style={SERIF}>KISSED</div>
            <div className="text-[0.6rem] tracking-[0.18em] uppercase mb-4" style={{ color: C.lavender }}>Trichology &amp; Functional Wellness</div>
            <p className="text-sm max-w-xs" style={{ color: "#D9CCEA" }} data-testid="footer-description">{f.description}</p>
            {(f.contactEmail || f.contactPhone) && (
              <p className="mt-3 text-sm" style={{ color: "#D9CCEA" }}>
                {f.contactEmail && <span className="block">{f.contactEmail}</span>}
                {f.contactPhone && <span className="block">{f.contactPhone}</span>}
              </p>
            )}
            {(f.socials || []).length > 0 && (
              <div className="mt-3 flex gap-3 text-sm" style={{ color: C.lavender }}>
                {f.socials.map((s) => <a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer" className="hover:text-white">{s.label}</a>)}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ color: C.lavender }}>Explore</div>
            <ul className="space-y-2 text-sm" style={{ color: "#D9CCEA" }}>
              {items.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
              <li><Link to={ROUTES.programs} className="hover:text-white" data-testid="footer-programs">Programs</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ color: C.lavender }}>Client &amp; Staff</div>
            <ul className="space-y-2 text-sm" style={{ color: "#D9CCEA" }}>
              <li><Link to={ROUTES.clientLogin} className="hover:text-white" data-testid="footer-client-login">Client Login</Link></li>
              <li><Link to={ROUTES.startHere} className="hover:text-white">Find My Starting Point</Link></li>
              <li><Link to={ROUTES.services} className="hover:text-white" data-testid="footer-existing-client">Existing Client Services</Link></li>
              <li><Link to={ROUTES.adminLogin} className="hover:text-white" data-testid="footer-staff-signin">Practitioner / Staff Sign In</Link></li>
              {(f.legal || []).map((l) => <li key={l.href}><a href={l.href} className="hover:text-white">{l.label}</a></li>)}
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 text-xs" style={{ color: "#B9A8CE", borderTop: "1px solid rgba(255,255,255,0.12)" }}>
          <p className="max-w-3xl" data-testid="footer-disclaimer">{f.disclaimer}</p>
          <p className="mt-3">© {new Date().getFullYear()} {f.copyright}</p>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children, stickyCta = true }) {
  const site = useSiteContent("site", DEFAULT_SITE);
  return (
    <div className={`min-h-screen flex flex-col bg-white ${stickyCta ? "pb-[72px] xl:pb-0" : ""}`} style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      <PublicNav nav={site.nav} />
      <main className="flex-1">{children}</main>
      <PublicFooter nav={site.nav} footer={site.footer} />
      {stickyCta && (
        <div className="xl:hidden fixed bottom-0 inset-x-0 z-40 p-3" style={{ background: "rgba(255,255,255,0.95)", borderTop: `1px solid ${C.line}`, backdropFilter: "blur(6px)" }} data-testid="sticky-mobile-cta">
          <PrimaryBtn to={site.nav?.ctaTo || ROUTES.navigator} className="w-full">{site.nav?.ctaLabel || "Get Started"} <ArrowRight className="h-4 w-4" /></PrimaryBtn>
        </div>
      )}
    </div>
  );
}
